import { useGetPartnerQuery } from '@/api/api.slice';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  STEP_ACCOUNT_URL,
  STEP_BRANDING_URL,
  STEP_PASSWORD_URL,
  STEP_PAYMENT_URL,
  STEP_REVIEW_URL,
} from '@/features/OnBoarding/constants.ts';
import {
  getOnBoardingState,
  setAllPartnerData,
  setOnBoardingMode,
} from '@/features/OnBoarding/OnBoarding.slice.ts';
import { Logger, Stepper, Typography } from '@arsnovasystems/ars-front-lib';
import { Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';

function defineActiveStep(pathname: string) {
  switch (pathname) {
    case STEP_PASSWORD_URL:
      return 0;
      break;
    case STEP_ACCOUNT_URL:
      return 1;
      break;
    case STEP_BRANDING_URL:
      return 2;
      break;
    case STEP_PAYMENT_URL:
      return 3;
      break;
    case STEP_REVIEW_URL:
      return 4;
      break;
    default:
      Logger.error('Unable to defined active steps, should not happen', {
        pathname,
      });
      return 0;
  }
}

/**
 * Leveraging a stepper component
 *
 * @returns Boarding step with the selection of the partner logo
 */
export const OnBoardingStepper = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const { onBoardingMode } = useAppSelector(getOnBoardingState);
  const { data: me } = useGetPartnerQuery();

  const steps: string[] = useMemo(() => {
    if (me?.signUpStatus === 'invited') {
      dispatch(setOnBoardingMode('invited'));
      return [
        t('ik.onboarding.stepOne.stepper.title'),
        t('ik.onboarding.stepTwo.stepper.title'),
        t('ik.onboarding.stepBranding.stepper.title'),
        t('ik.onboarding.stepThree.stepper.title'),
        t('ik.onboarding.lastStep.stepper.title'),
      ];
    } else if (me?.signUpStatus === 'to-migrate-invited') {
      dispatch(setOnBoardingMode('to-migrate'));
      return [
        t('ik.onboarding.stepOne.stepper.title'),
        t('ik.onboarding.migrate.stepTwo.stepper.title'),
        t('ik.onboarding.stepBranding.stepper.title'),
        t('ik.onboarding.stepThree.stepper.title'),
        t('ik.onboarding.lastStep.stepper.title'),
      ];
    }

    return [];
  }, [dispatch, me?.signUpStatus, t]);

  const activeStep = defineActiveStep(pathname);

  useEffect(() => {
    if (!me) return;
    dispatch(setAllPartnerData(me));
  }, [dispatch, me]);

  return (
    <Stack
      sx={{ width: '100%', minHeight: '200px', alignItems: 'center', gap: 2 }}
    >
      <Typography variant='h4'>
        {onBoardingMode === 'invited' ?
          t('ik.complete.your.account.creation')
        : t('ik.migrate.your.account')}
      </Typography>
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        steps={steps}
        sx={{
          width: '100%',
          paddingX: 4,
          marginTop: 3,
        }}
      />
      <Outlet />
    </Stack>
  );
};
